import React, { useState, useMemo, useEffect, useCallback } from 'react';

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// mappings.py 기반 DTO 매핑 데이터 (표준 매핑 정보)
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const DEFAULT_HEADER_MAPPINGS = {
  invoice_number: {
    standard: "invoice_number",
    label: "인보이스번호",
    variations: [
      "invoice number", "invoice no", "inv no", "doc no", "doc#",
      "reference number", "document number", "commercial invoice no",
      "invoice ref", "ref no", "invoice#", "inv#", "invoice no.",
      "inv. no.", "reference no.", "ref.", "inv ref", "invoice reference",
      "document no", "doc number"
    ]
  },
  invoice_date: {
    standard: "invoice_date",
    label: "인보이스일자",
    variations: [
      "invoice date", "date", "issued date", "document date",
      "inv date", "date of invoice", "preparation date", "issue date"
    ]
  },
  bl_number: {
    standard: "bl_number",
    label: "B/L·운송장번호",
    variations: [
      "bl number", "bl no", "bill of lading number", "bill of lading no",
      "b/l number", "b/l no", "master bl", "house bl", "bl#",
      "b/l#", "mbl", "hbl", "mbl no", "hbl no", "lading number",
      "bill of lading", "ocean bl", "sea waybill",
      "waybill", "air waybill", "awb", "way bill", "air way bill",
      "air consignment note", "consignment note", "waybill number",
      "awb number", "waybill no", "awb no", "tracking number",
      "tracking no", "air waybill no", "hawb", "mawb",
      "hawb no", "mawb no", "air waybill no. / tracking no."
    ]
  },
  // waybill은 bl_number로 통합됨 (해상/항공 구분 없이 운송서류번호 1개 필드)
  seller_name: {
    standard: "seller_name",
    label: "수출자 (Shipper)",
    variations: [
      "seller", "shipper", "consignor", "exporter", "supplier",
      "sold by", "vendor", "from", "sender", "manufacturer",
      "shipped by", "export by", "beneficiary"
    ]
  },
  buyer_name: {
    standard: "buyer_name",
    label: "수입자 (Consignee)",
    variations: [
      "buyer", "consignee", "importer", "ship to", "deliver to",
      "recipient", "customer", "purchased by", "ordered by", "client",
      "bill to", "applicant", "payment party"
    ]
  },
  description: {
    standard: "description",
    label: "품명/사양",
    variations: [
      "description", "spec", "specification", "desc", "item description",
      "commodity", "goods", "product description", "item name", "product",
      "article", "particulars", "details"
    ]
  },
  composition: {
    standard: "composition",
    label: "성분/재질",
    variations: [
      "composition", "material", "ingredient", "component", "content",
      "material composition", "fabric", "substance", "chemical composition",
      "raw material", "contents", "comp", "material spec"
    ]
  },
  part_number: {
    standard: "partno",
    label: "품번/모델",
    variations: [
      "item no", "item no.", "part no", "part no.", "model", "model no",
      "part number", "item number", "product code", "sku",
      "article no", "goods no", "code", "style no", "item code"
    ]
  },
  quantity: {
    standard: "quantity",
    label: "수량",
    variations: [
      "qty", "pcs", "pieces", "units", "count", "number of items",
      "no. of pcs", "no. of units", "order quantity", "total quantity",
      "shipping qty", "quantity ordered"
    ]
  },
  unit_type: {
    standard: "unit_type",
    label: "수량단위",
    variations: [
      "unit", "unit type", "uom", "unit of measure", "measurement unit",
      "packing unit", "unit(s)", "단위", "drum", "pcs", "kg", "ea",
      "bag", "roll", "box", "set", "pack", "bundle", "carton"
    ]
  },
  unit_price: {
    standard: "unit_price",
    label: "단가",
    variations: [
      "unit price", "price", "unit value", "price per unit", "rate",
      "item price", "unit cost", "price/unit", "rate per unit",
      "unit price(usd)", "unit price (usd)", "price(usd)"
    ]
  },
  amount: {
    standard: "amount",
    label: "금액",
    variations: [
      "amount", "total", "total amount", "subtotal", "sum", "line total",
      "extended amount", "value", "total value", "extended price",
      "net amount", "item total", "ext amount"
    ]
  },
  net_weight: {
    standard: "net_weight",
    label: "순중량",
    variations: [
      "net weight", "n.weight", "net wt", "n.w.", "n/w", "nett weight",
      "net weight kg", "weight net", "net mass", "actual weight", "product weight"
    ]
  },
  gross_weight: {
    standard: "gross_weight",
    label: "총중량",
    variations: [
      "gross weight", "g.weight", "gross wt", "g.w.", "g/w", "total weight",
      "gross weight kg", "weight gross", "gross mass", "shipping weight", "packed weight"
    ]
  },
  package_count: {
    standard: "package_count",
    label: "포장수량",
    variations: [
      "package count", "no of packages", "number of packages", "total packages",
      "pkg", "packages", "cartons", "boxes", "pallets", "cases",
      "pack count", "carton count", "pkgs"
    ]
  },
  hscode: {
    standard: "hs_code",
    label: "HS코드",
    variations: [
      "hs code", "hs_code", "tariff", "harmonized code", "hs", "tariff code",
      "tariff number", "customs code", "commodity code", "hts code", "statistical code"
    ]
  },
  origin: {
    standard: "coo",
    label: "원산지",
    variations: [
      "origin", "country of origin", "made in", "origin country", "coo", "c/o",
      "place of origin", "manufactured in", "source", "produced in", "origin of goods"
    ]
  },
  incoterms: {
    standard: "incoterms",
    label: "인코텀즈",
    variations: [
      "incoterms", "delivery terms", "trade terms", "shipping terms",
      "terms of delivery", "commercial terms", "icc terms", "delivery condition",
      "FOB", "CIF", "CFR", "EXW", "DDP", "DAP", "FCA"
    ]
  },
  currency: {
    standard: "currency",
    label: "통화",
    variations: [
      "currency", "currency code", "invoice currency", "payment currency",
      "monetary unit", "currency type", "denomination", "cur"
    ]
  },
  payment_terms: {
    standard: "payment_terms",
    label: "결제조건",
    variations: [
      "payment terms", "terms of payment", "payment condition",
      "payment method", "settlement terms", "credit terms", "terms", "payment"
    ]
  },
  order_no: {
    standard: "order_no",
    label: "주문번호",
    variations: [
      "order no", "purchase order no", "po no", "order number",
      "customer order no", "sales order no", "po number", "p/o no"
    ]
  },
  port_of_loading: {
    standard: "port_of_loading",
    label: "선적항",
    variations: [
      "port of loading", "loading port", "pol", "departure port",
      "port of departure", "loading place", "shipped from port", "origin port"
    ]
  },
  port_of_discharge: {
    standard: "port_of_discharge",
    label: "양륙항",
    variations: [
      "port of discharge", "discharge port", "pod", "arrival port",
      "destination port", "port of destination", "unloading port", "final port"
    ]
  },
  vessel: {
    standard: "vessel",
    label: "선박/항공편",
    variations: [
      "vessel", "vessel name", "ship name", "carrier", "vessel/flight",
      "transport vehicle", "conveyance", "shipping line", "flight number"
    ]
  },
  marks_numbers: {
    standard: "marks_numbers",
    label: "화인 (Marks)",
    variations: [
      "marks and numbers", "marks & nos", "shipping marks",
      "container marks", "package marks", "marking",
      "external marks", "identification marks"
    ]
  }
};

const DEFAULT_DOC_TYPE_PATTERNS = {
  COMMERCIAL_INVOICE: { label: "상업송장 (CI)", patterns: ["COMMERCIAL INVOICE", "COMM INVOICE", "CI", "C/I", "C.I.", "INVOICE"] },
  PACKING_LIST: { label: "포장명세서 (PL)", patterns: ["PACKING LIST", "PACK LIST", "P/L", "PL", "PACKING SLIP", "PACKING NOTE"] },
  BILL_OF_LADING: { label: "B/L (해상/항공)", patterns: ["BILL OF LADING", "B/L", "BOL", "OCEAN B/L", "SEA WAYBILL", "AIR WAYBILL", "AWB", "AIRWAY BILL", "AIR CONSIGNMENT NOTE"] },
  CERTIFICATE_OF_ORIGIN: { label: "원산지증명서 (C/O)", patterns: ["CERTIFICATE OF ORIGIN", "C/O", "ORIGIN CERTIFICATE"] },
  COMBINED: { label: "송장+패킹 (INV/PKG)", patterns: ["IN/PK", "INVOICE/PACKING LIST", "COMBINED INVOICE AND PACKING LIST"] },
  FTA_DOCUMENT: { label: "FTA 증명서", patterns: ["FTA CERTIFICATE", "FORM K", "FORM AK", "FORM E"] },
  KC_CERTIFICATE: { label: "KC 인증서", patterns: ["KC CERTIFICATE", "KC MARK", "KC CERT"] }
};

const DEFAULT_UNIT_STANDARDIZATION = {
  PC: { label: "개/수량", variations: ["PC", "PCS", "PIECE", "PIECES", "EA", "EACH"] },
  KG: { label: "킬로그램", variations: ["KG", "KILOGRAM", "KILOGRAMS", "KGS", "KG.", "KG/S"] },
  M: { label: "미터", variations: ["M", "METER", "METERS", "MTR", "MTRS"] },
  CTN: { label: "카톤/박스", variations: ["CTN", "CARTON", "CARTONS", "CTNS", "CT"] },
  SET: { label: "세트", variations: ["SET", "SETS", "KIT", "KITS"] }
};

const DEFAULT_COUNTRY_CODES = {
  CN: { name: "중국 (China)", variations: ["CHINA", "MADE IN CHINA", "P.R.C", "PRC"] },
  KR: { name: "한국 (Korea)", variations: ["KOREA", "SOUTH KOREA", "S.KOREA", "S.KR", "REPUBLIC OF KR", "REPUBLIC OF KOREA"] },
  US: { name: "미국 (USA)", variations: ["UNITED STATES", "USA", "U.S.A", "US"] },
  VN: { name: "베트남 (Vietnam)", variations: ["VIETNAM", "VIET NAM", "MADE IN VN"] },
  JP: { name: "일본 (Japan)", variations: ["JAPAN", "MADE IN JAPAN", "MADE IN JP", "JAPANESE ORIGIN"] },
  TW: { name: "대만 (Taiwan)", variations: ["TAIWAN", "MADE IN TAIWAN", "R.O.C"] },
  TH: { name: "태국 (Thailand)", variations: ["THAILAND", "MADE IN THAILAND"] },
  ID: { name: "인도네시아 (Indonesia)", variations: ["INDONESIA", "MADE IN INDONESIA"] },
  IN: { name: "인도 (India)", variations: ["INDIA", "MADE IN INDIA"] },
  MY: { name: "말레이시아 (Malaysia)", variations: ["MALAYSIA", "MADE IN MALAYSIA", "MADE IN ML"] },
  DE: { name: "독일 (Germany)", variations: ["GERMANY", "DEUTSCHLAND", "MADE IN GERMANY"] },
  NL: { name: "네덜란드 (Netherlands)", variations: ["NETHERLANDS", "HOLLAND", "European Union"] }
};

// localStorage 키
const LS_KEY_HEADER = 'dtoHeaderMappings';
const LS_KEY_DOCTYPE = 'dtoDocTypePatterns';
const LS_KEY_UNIT = 'dtoUnitStandardization';
const LS_KEY_COUNTRY = 'dtoCountryCodes';

// localStorage 로더 (없으면 기본값)
const loadFromLS = (key, defaults) => {
  try {
    const saved = localStorage.getItem(key);
    if (saved) return JSON.parse(saved);
  } catch (e) { /* ignore */ }
  return JSON.parse(JSON.stringify(defaults));
};
const loadHeaderMappings = () => loadFromLS(LS_KEY_HEADER, DEFAULT_HEADER_MAPPINGS);
const loadDocTypes = () => loadFromLS(LS_KEY_DOCTYPE, DEFAULT_DOC_TYPE_PATTERNS);
const loadUnits = () => loadFromLS(LS_KEY_UNIT, DEFAULT_UNIT_STANDARDIZATION);
const loadCountries = () => loadFromLS(LS_KEY_COUNTRY, DEFAULT_COUNTRY_CODES);

// 메인 컴포넌트

const TABS = [
  { id: 'header', label: '헤더 매핑' },
  { id: 'doctype', label: '문서유형' },
  { id: 'unit', label: '단위 표준화' },
  { id: 'country', label: '국가 코드' },
];

const MappingTableViewer = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('header');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedField, setExpandedField] = useState(null);
  const [headerMappings, setHeaderMappings] = useState(loadHeaderMappings);
  const [docTypes, setDocTypes] = useState(loadDocTypes);
  const [units, setUnits] = useState(loadUnits);
  const [countries, setCountries] = useState(loadCountries);
  const [newVariationText, setNewVariationText] = useState('');
  const [showAddField, setShowAddField] = useState(false);
  const [newFieldKey, setNewFieldKey] = useState('');
  const [newFieldLabel, setNewFieldLabel] = useState('');

  // 범용 저장
  const saveData = useCallback((lsKey, setter) => (data) => {
    setter(data);
    localStorage.setItem(lsKey, JSON.stringify(data));
  }, []);
  const saveHeaderMappings = useCallback(saveData(LS_KEY_HEADER, setHeaderMappings), []);
  const saveDocTypes = useCallback(saveData(LS_KEY_DOCTYPE, setDocTypes), []);
  const saveUnits = useCallback(saveData(LS_KEY_UNIT, setUnits), []);
  const saveCountries = useCallback(saveData(LS_KEY_COUNTRY, setCountries), []);

  // 범용: 항목의 배열 필드에 값 추가 (variations 또는 patterns)
  const addItemToArray = useCallback((dataObj, saveFunc, fieldKey, arrKey, text) => {
    if (!text.trim()) return;
    const updated = { ...dataObj };
    if (updated[fieldKey]) {
      const arr = updated[fieldKey][arrKey] || [];
      const upper = text.trim().toUpperCase();
      if (!arr.some(v => v.toUpperCase() === upper)) {
        updated[fieldKey] = { ...updated[fieldKey], [arrKey]: [...arr, text.trim()] };
        saveFunc(updated);
      }
    }
    setNewVariationText('');
  }, []);

  // 범용: 항목의 배열 필드에서 값 제거
  const removeItemFromArray = useCallback((dataObj, saveFunc, fieldKey, arrKey, idx) => {
    const updated = { ...dataObj };
    if (updated[fieldKey]) {
      updated[fieldKey] = { ...updated[fieldKey], [arrKey]: updated[fieldKey][arrKey].filter((_, i) => i !== idx) };
      saveFunc(updated);
    }
  }, []);

  // 범용: 항목 삭제
  const removeEntry = useCallback((dataObj, saveFunc, fieldKey) => {
    const updated = { ...dataObj };
    delete updated[fieldKey];
    saveFunc(updated);
    if (expandedField === fieldKey) setExpandedField(null);
  }, [expandedField]);

  // 범용: 항목 추가
  const addEntry = useCallback((dataObj, saveFunc, key, entryData) => {
    if (!key || dataObj[key]) return;
    saveFunc({ ...dataObj, [key]: entryData });
    setNewFieldKey('');
    setNewFieldLabel('');
    setShowAddField(false);
    setExpandedField(key);
  }, []);

  // 헤더 매핑 전용 함수 (기존 호환)
  const addVariation = useCallback((fk, txt) => addItemToArray(headerMappings, saveHeaderMappings, fk, 'variations', txt), [headerMappings, saveHeaderMappings, addItemToArray]);
  const removeVariation = useCallback((fk, idx) => removeItemFromArray(headerMappings, saveHeaderMappings, fk, 'variations', idx), [headerMappings, saveHeaderMappings, removeItemFromArray]);
  const removeField = useCallback((fk) => removeEntry(headerMappings, saveHeaderMappings, fk), [headerMappings, saveHeaderMappings, removeEntry]);
  const addField = useCallback(() => {
    const key = newFieldKey.trim().toLowerCase().replace(/\s+/g, '_');
    if (!key || !newFieldLabel.trim()) return;
    addEntry(headerMappings, saveHeaderMappings, key, { standard: key, label: newFieldLabel.trim(), variations: [] });
  }, [headerMappings, newFieldKey, newFieldLabel, saveHeaderMappings, addEntry]);

  // 기본값 복원 (탭별)
  const resetCurrentTab = useCallback(() => {
    if (activeTab === 'header') { saveHeaderMappings(JSON.parse(JSON.stringify(DEFAULT_HEADER_MAPPINGS))); }
    else if (activeTab === 'doctype') { saveDocTypes(JSON.parse(JSON.stringify(DEFAULT_DOC_TYPE_PATTERNS))); }
    else if (activeTab === 'unit') { saveUnits(JSON.parse(JSON.stringify(DEFAULT_UNIT_STANDARDIZATION))); }
    else if (activeTab === 'country') { saveCountries(JSON.parse(JSON.stringify(DEFAULT_COUNTRY_CODES))); }
    setExpandedField(null);
  }, [activeTab, saveHeaderMappings, saveDocTypes, saveUnits, saveCountries]);

  // 필터링
  const filteredHeaderMappings = useMemo(() => {
    if (!searchQuery) return Object.entries(headerMappings);
    const q = searchQuery.toLowerCase();
    return Object.entries(headerMappings).filter(([key, val]) =>
      key.includes(q) || val.label.includes(q) || val.standard.includes(q) ||
      val.variations.some(v => v.toLowerCase().includes(q))
    );
  }, [searchQuery, headerMappings]);

  // 통계
  const stats = useMemo(() => {
    const totalVariations = Object.values(headerMappings).reduce((sum, v) => sum + v.variations.length, 0);
    const totalFields = Object.keys(headerMappings).length;
    const totalDocTypes = Object.keys(docTypes).length;
    const totalCountries = Object.keys(countries).length;
    return { totalVariations, totalFields, totalDocTypes, totalCountries };
  }, [headerMappings, docTypes, countries]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/30" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-[720px] max-h-[85vh] flex flex-col border border-gray-200" onClick={e => e.stopPropagation()}>
        
        {/* Header */}
        <div className="px-5 py-3 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-[15px] font-medium text-gray-900">데이터 변환표(DTO)</h2>
              <p className="text-[12px] text-gray-500 mt-0.5">
                {stats.totalFields}개 필드 · {stats.totalVariations}개 변형 · {stats.totalDocTypes}개 문서유형 · {stats.totalCountries}개국
              </p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-sm">✕</button>
          </div>

          {/* Tabs */}
          <div className="flex gap-0 mt-3 border-b border-gray-200 -mb-[1px]">
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => { setActiveTab(tab.id); setSearchQuery(''); setExpandedField(null); }}
                className={`px-4 py-2 text-[12px] font-medium transition-colors border-b-2 ${
                  activeTab === tab.id
                    ? 'border-gray-900 text-gray-900'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Search (Header tab only) */}
        {activeTab === 'header' && (
          <div className="px-5 py-2 border-b border-gray-100">
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="검색 (예: invoice, qty, shipper...)"
              className="w-full text-[12px] px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-gray-500"
            />
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto">

          {/* 헤더 매핑 */}
          {activeTab === 'header' && (
            <div className="p-4 space-y-1">
              {/* Flow */}
              <div className="mb-3 py-2 flex items-center justify-center gap-3 text-[12px] text-gray-500">
                <span className="px-3 py-1 bg-gray-100 rounded text-gray-700">원본 문서</span>
                <span>→</span>
                <span className="px-3 py-1 bg-gray-100 rounded text-gray-700">AI 추출</span>
                <span>→</span>
                <span className="px-3 py-1 bg-gray-900 text-white rounded font-medium">매핑 테이블</span>
                <span>→</span>
                <span className="px-3 py-1 bg-gray-100 rounded text-gray-700">표준 출력(DTO)</span>
              </div>

              {filteredHeaderMappings.map(([key, field]) => {
                const isExpanded = expandedField === key;
                return (
                  <div key={key} className={`rounded border transition-all ${isExpanded ? 'border-gray-400' : 'border-gray-200 hover:border-gray-300'}`}>
                    <div className="flex items-center">
                      <button
                        onClick={() => setExpandedField(isExpanded ? null : key)}
                        className="flex-1 flex items-center gap-3 px-4 py-2 text-left"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-[13px] font-medium text-gray-900">{field.label}</span>
                            <code className="text-[11px] px-1.5 py-0.5 bg-gray-100 text-gray-800 rounded font-mono font-bold">{field.standard}</code>
                          </div>
                          {!isExpanded && (
                            <p className="text-[11px] text-gray-400 truncate mt-0.5">
                              {field.variations.slice(0, 6).join(', ')}
                              {field.variations.length > 6 && ` +${field.variations.length - 6}`}
                            </p>
                          )}
                        </div>
                        <span className="text-[11px] text-gray-400 font-mono">{field.variations.length}</span>
                        <span className={`text-xs text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}>▾</span>
                      </button>
                      {/* 필드 삭제 버튼 */}
                      <button
                        onClick={(e) => { e.stopPropagation(); if (window.confirm(`"${field.label}" 필드를 삭제하시겠습니까?`)) removeField(key); }}
                        className="px-2 py-2 text-gray-300 hover:text-gray-600 text-xs flex-shrink-0"
                        title="필드 삭제"
                      >✕</button>
                    </div>

                    {isExpanded && (
                      <div className="px-4 pb-3 pt-0">
                        {/* 변형 태그 (삭제 가능) */}
                        <div className="flex flex-wrap gap-1.5">
                          {field.variations.map((v, i) => (
                            <span key={i} className="inline-flex items-center gap-1 text-[12px] px-2.5 py-1 bg-gray-100 text-gray-800 rounded border border-gray-200 group">
                              {v}
                              <button
                                onClick={() => removeVariation(key, i)}
                                className="text-gray-300 hover:text-gray-700 text-[10px] leading-none group-hover:text-gray-500"
                                title="변형 삭제"
                              >✕</button>
                            </span>
                          ))}
                        </div>
                        {/* 변형 추가 입력 */}
                        <div className="flex items-center gap-2 mt-2">
                          <input
                            type="text"
                            value={expandedField === key ? newVariationText : ''}
                            onChange={e => setNewVariationText(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') { addVariation(key, newVariationText); } }}
                            placeholder="새 변형 추가 (Enter)"
                            className="flex-1 text-[11px] px-2.5 py-1.5 border border-gray-300 rounded focus:outline-none focus:border-gray-500"
                          />
                          <button
                            onClick={() => addVariation(key, newVariationText)}
                            className="text-[11px] px-3 py-1.5 rounded bg-gray-900 text-white hover:bg-gray-800 transition-colors"
                          >추가</button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}

              {filteredHeaderMappings.length === 0 && (
                <div className="text-center text-gray-400 text-[11px] py-8">검색 결과 없음</div>
              )}

              {/* 새 필드 추가 */}
              {!searchQuery && (
                <div className="pt-2">
                  {!showAddField ? (
                    <button
                      onClick={() => setShowAddField(true)}
                      className="w-full text-[12px] py-2 rounded border border-dashed border-gray-300 text-gray-500 hover:border-gray-400 hover:text-gray-700 transition-colors"
                    >
                      + 새 필드 추가
                    </button>
                  ) : (
                    <div className="rounded border border-gray-300 p-3 space-y-2">
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={newFieldLabel}
                          onChange={e => setNewFieldLabel(e.target.value)}
                          placeholder="라벨 (예: 성분/재질)"
                          className="flex-1 text-[11px] px-2.5 py-1.5 border border-gray-300 rounded focus:outline-none focus:border-gray-500"
                        />
                        <input
                          type="text"
                          value={newFieldKey}
                          onChange={e => setNewFieldKey(e.target.value)}
                          placeholder="키 (예: composition)"
                          className="flex-1 text-[11px] px-2.5 py-1.5 border border-gray-300 rounded focus:outline-none focus:border-gray-500 font-mono"
                        />
                      </div>
                      <div className="flex gap-2 justify-end">
                        <button onClick={() => { setShowAddField(false); setNewFieldKey(''); setNewFieldLabel(''); }}
                          className="text-[11px] px-3 py-1.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-50"
                        >취소</button>
                        <button onClick={addField}
                          className="text-[11px] px-3 py-1.5 rounded bg-gray-900 text-white hover:bg-gray-800"
                        >추가</button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* 문서유형 (편집 가능) */}
          {activeTab === 'doctype' && (
            <div className="p-4 space-y-1">
              <p className="text-[11px] text-gray-500 mb-3">문서 1페이지를 분석하여 아래 패턴으로 문서 유형을 자동 분류합니다.</p>
              {Object.entries(docTypes).map(([key, doc]) => {
                const isExpanded = expandedField === key;
                return (
                  <div key={key} className={`rounded border transition-all ${isExpanded ? 'border-gray-400' : 'border-gray-200 hover:border-gray-300'}`}>
                    <div className="flex items-center">
                      <button onClick={() => setExpandedField(isExpanded ? null : key)} className="flex-1 flex items-center gap-3 px-4 py-2 text-left">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-[13px] font-medium text-gray-900">{doc.label}</span>
                            <code className="text-[10px] text-gray-800 font-mono font-bold">{key}</code>
                          </div>
                          {!isExpanded && (
                            <p className="text-[11px] text-gray-400 truncate mt-0.5">{doc.patterns.join(', ')}</p>
                          )}
                        </div>
                        <span className="text-[11px] text-gray-400 font-mono">{doc.patterns.length}</span>
                        <span className={`text-xs text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}>▾</span>
                      </button>
                      <button onClick={() => { if (window.confirm(`"${doc.label}" 삭제?`)) removeEntry(docTypes, saveDocTypes, key); }}
                        className="px-2 py-2 text-gray-300 hover:text-gray-600 text-xs flex-shrink-0" title="삭제">✕</button>
                    </div>
                    {isExpanded && (
                      <div className="px-4 pb-3 pt-0">
                        <div className="flex flex-wrap gap-1.5">
                          {doc.patterns.map((p, i) => (
                            <span key={i} className="inline-flex items-center gap-1 text-[12px] px-2.5 py-1 bg-gray-100 text-gray-800 rounded border border-gray-200 group">
                              {p}
                              <button onClick={() => removeItemFromArray(docTypes, saveDocTypes, key, 'patterns', i)}
                                className="text-gray-300 hover:text-gray-700 text-[10px] group-hover:text-gray-500">✕</button>
                            </span>
                          ))}
                        </div>
                        <div className="flex items-center gap-2 mt-2">
                          <input type="text" value={expandedField === key ? newVariationText : ''} onChange={e => setNewVariationText(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') { addItemToArray(docTypes, saveDocTypes, key, 'patterns', newVariationText); } }}
                            placeholder="새 패턴 추가 (Enter)" className="flex-1 text-[11px] px-2.5 py-1.5 border border-gray-300 rounded focus:outline-none focus:border-gray-500" />
                          <button onClick={() => addItemToArray(docTypes, saveDocTypes, key, 'patterns', newVariationText)}
                            className="text-[11px] px-3 py-1.5 rounded bg-gray-900 text-white hover:bg-gray-800">추가</button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              {/* 새 문서유형 추가 */}
              {!showAddField ? (
                <button onClick={() => setShowAddField(true)}
                  className="w-full text-[12px] py-2 rounded border border-dashed border-gray-300 text-gray-500 hover:border-gray-400 hover:text-gray-700 mt-2">+ 새 문서유형 추가</button>
              ) : (
                <div className="rounded border border-gray-300 p-3 space-y-2 mt-2">
                  <div className="flex gap-2">
                    <input type="text" value={newFieldLabel} onChange={e => setNewFieldLabel(e.target.value)} placeholder="라벨 (예: 기타문서)"
                      className="flex-1 text-[11px] px-2.5 py-1.5 border border-gray-300 rounded focus:outline-none focus:border-gray-500" />
                    <input type="text" value={newFieldKey} onChange={e => setNewFieldKey(e.target.value)} placeholder="키 (예: OTHER)"
                      className="flex-1 text-[11px] px-2.5 py-1.5 border border-gray-300 rounded focus:outline-none focus:border-gray-500 font-mono" />
                  </div>
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => { setShowAddField(false); setNewFieldKey(''); setNewFieldLabel(''); }}
                      className="text-[11px] px-3 py-1.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-50">취소</button>
                    <button onClick={() => { const k = newFieldKey.trim().toUpperCase().replace(/\s+/g, '_'); if (k && newFieldLabel.trim()) addEntry(docTypes, saveDocTypes, k, { label: newFieldLabel.trim(), patterns: [] }); }}
                      className="text-[11px] px-3 py-1.5 rounded bg-gray-900 text-white hover:bg-gray-800">추가</button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* 단위 표준화 (편집 가능) */}
          {activeTab === 'unit' && (
            <div className="p-4 space-y-1">
              <p className="text-[11px] text-gray-500 mb-3">추출된 수량 단위를 표준 코드로 자동 변환합니다.</p>
              {Object.entries(units).map(([code, unit]) => {
                const isExpanded = expandedField === code;
                return (
                  <div key={code} className={`rounded border transition-all ${isExpanded ? 'border-gray-400' : 'border-gray-200 hover:border-gray-300'}`}>
                    <div className="flex items-center">
                      <button onClick={() => setExpandedField(isExpanded ? null : code)} className="flex-1 flex items-center gap-3 px-4 py-2 text-left">
                        <div className="w-10 h-10 flex items-center justify-center bg-gray-100 text-gray-900 font-bold text-[11px] rounded flex-shrink-0">{code}</div>
                        <div className="flex-1 min-w-0">
                          <span className="text-[13px] font-medium text-gray-900">{unit.label}</span>
                          {!isExpanded && (
                            <p className="text-[11px] text-gray-400 truncate mt-0.5">{unit.variations.join(', ')}</p>
                          )}
                        </div>
                        <span className="text-[11px] text-gray-400 font-mono">{unit.variations.length}</span>
                        <span className={`text-xs text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}>▾</span>
                      </button>
                      <button onClick={() => { if (window.confirm(`"${unit.label}" 삭제?`)) removeEntry(units, saveUnits, code); }}
                        className="px-2 py-2 text-gray-300 hover:text-gray-600 text-xs flex-shrink-0" title="삭제">✕</button>
                    </div>
                    {isExpanded && (
                      <div className="px-4 pb-3 pt-0">
                        <div className="flex flex-wrap gap-1.5">
                          {unit.variations.map((v, i) => (
                            <span key={i} className="inline-flex items-center gap-1 text-[12px] px-2.5 py-1 bg-gray-100 text-gray-800 rounded border border-gray-200 group">
                              {v}
                              <button onClick={() => removeItemFromArray(units, saveUnits, code, 'variations', i)}
                                className="text-gray-300 hover:text-gray-700 text-[10px] group-hover:text-gray-500">✕</button>
                            </span>
                          ))}
                        </div>
                        <div className="flex items-center gap-2 mt-2">
                          <input type="text" value={expandedField === code ? newVariationText : ''} onChange={e => setNewVariationText(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') { addItemToArray(units, saveUnits, code, 'variations', newVariationText); } }}
                            placeholder="새 변형 추가 (Enter)" className="flex-1 text-[11px] px-2.5 py-1.5 border border-gray-300 rounded focus:outline-none focus:border-gray-500" />
                          <button onClick={() => addItemToArray(units, saveUnits, code, 'variations', newVariationText)}
                            className="text-[11px] px-3 py-1.5 rounded bg-gray-900 text-white hover:bg-gray-800">추가</button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              {!showAddField ? (
                <button onClick={() => setShowAddField(true)}
                  className="w-full text-[12px] py-2 rounded border border-dashed border-gray-300 text-gray-500 hover:border-gray-400 hover:text-gray-700 mt-2">+ 새 단위 추가</button>
              ) : (
                <div className="rounded border border-gray-300 p-3 space-y-2 mt-2">
                  <div className="flex gap-2">
                    <input type="text" value={newFieldLabel} onChange={e => setNewFieldLabel(e.target.value)} placeholder="라벨 (예: 리터)"
                      className="flex-1 text-[11px] px-2.5 py-1.5 border border-gray-300 rounded focus:outline-none focus:border-gray-500" />
                    <input type="text" value={newFieldKey} onChange={e => setNewFieldKey(e.target.value)} placeholder="코드 (예: LTR)"
                      className="flex-1 text-[11px] px-2.5 py-1.5 border border-gray-300 rounded focus:outline-none focus:border-gray-500 font-mono" />
                  </div>
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => { setShowAddField(false); setNewFieldKey(''); setNewFieldLabel(''); }}
                      className="text-[11px] px-3 py-1.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-50">취소</button>
                    <button onClick={() => { const k = newFieldKey.trim().toUpperCase(); if (k && newFieldLabel.trim()) addEntry(units, saveUnits, k, { label: newFieldLabel.trim(), variations: [] }); }}
                      className="text-[11px] px-3 py-1.5 rounded bg-gray-900 text-white hover:bg-gray-800">추가</button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* 국가 코드 (편집 가능) */}
          {activeTab === 'country' && (
            <div className="p-4 space-y-1">
              <p className="text-[11px] text-gray-500 mb-3">원산지 텍스트를 ISO 2자리 국가 코드로 변환합니다. {Object.keys(countries).length}개국 대응.</p>
              {Object.entries(countries).map(([code, country]) => {
                const isExpanded = expandedField === code;
                return (
                  <div key={code} className={`rounded border transition-all ${isExpanded ? 'border-gray-400' : 'border-gray-200 hover:border-gray-300'}`}>
                    <div className="flex items-center">
                      <button onClick={() => setExpandedField(isExpanded ? null : code)} className="flex-1 flex items-center gap-3 px-4 py-2 text-left">
                        <div className="w-8 h-8 flex items-center justify-center bg-gray-100 text-gray-900 font-bold text-[10px] rounded flex-shrink-0">{code}</div>
                        <div className="flex-1 min-w-0">
                          <span className="text-[12px] font-medium text-gray-900">{country.name}</span>
                          {!isExpanded && (
                            <p className="text-[10px] text-gray-400 truncate mt-0.5">{country.variations.join(', ')}</p>
                          )}
                        </div>
                        <span className="text-[11px] text-gray-400 font-mono">{country.variations.length}</span>
                        <span className={`text-xs text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}>▾</span>
                      </button>
                      <button onClick={() => { if (window.confirm(`"${country.name}" 삭제?`)) removeEntry(countries, saveCountries, code); }}
                        className="px-2 py-2 text-gray-300 hover:text-gray-600 text-xs flex-shrink-0" title="삭제">✕</button>
                    </div>
                    {isExpanded && (
                      <div className="px-4 pb-3 pt-0">
                        <div className="flex flex-wrap gap-1.5">
                          {country.variations.map((v, i) => (
                            <span key={i} className="inline-flex items-center gap-1 text-[12px] px-2.5 py-1 bg-gray-100 text-gray-800 rounded border border-gray-200 group">
                              {v}
                              <button onClick={() => removeItemFromArray(countries, saveCountries, code, 'variations', i)}
                                className="text-gray-300 hover:text-gray-700 text-[10px] group-hover:text-gray-500">✕</button>
                            </span>
                          ))}
                        </div>
                        <div className="flex items-center gap-2 mt-2">
                          <input type="text" value={expandedField === code ? newVariationText : ''} onChange={e => setNewVariationText(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') { addItemToArray(countries, saveCountries, code, 'variations', newVariationText); } }}
                            placeholder="새 변형 추가 (Enter)" className="flex-1 text-[11px] px-2.5 py-1.5 border border-gray-300 rounded focus:outline-none focus:border-gray-500" />
                          <button onClick={() => addItemToArray(countries, saveCountries, code, 'variations', newVariationText)}
                            className="text-[11px] px-3 py-1.5 rounded bg-gray-900 text-white hover:bg-gray-800">추가</button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              {!showAddField ? (
                <button onClick={() => setShowAddField(true)}
                  className="w-full text-[12px] py-2 rounded border border-dashed border-gray-300 text-gray-500 hover:border-gray-400 hover:text-gray-700 mt-2">+ 새 국가 추가</button>
              ) : (
                <div className="rounded border border-gray-300 p-3 space-y-2 mt-2">
                  <div className="flex gap-2">
                    <input type="text" value={newFieldLabel} onChange={e => setNewFieldLabel(e.target.value)} placeholder="이름 (예: 영국 (UK))"
                      className="flex-1 text-[11px] px-2.5 py-1.5 border border-gray-300 rounded focus:outline-none focus:border-gray-500" />
                    <input type="text" value={newFieldKey} onChange={e => setNewFieldKey(e.target.value)} placeholder="코드 (예: GB)"
                      className="flex-1 text-[11px] px-2.5 py-1.5 border border-gray-300 rounded focus:outline-none focus:border-gray-500 font-mono" />
                  </div>
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => { setShowAddField(false); setNewFieldKey(''); setNewFieldLabel(''); }}
                      className="text-[11px] px-3 py-1.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-50">취소</button>
                    <button onClick={() => { const k = newFieldKey.trim().toUpperCase(); if (k && newFieldLabel.trim()) addEntry(countries, saveCountries, k, { name: newFieldLabel.trim(), variations: [] }); }}
                      className="text-[11px] px-3 py-1.5 rounded bg-gray-900 text-white hover:bg-gray-800">추가</button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-gray-400">localStorage 자동 저장</span>
            <button
              onClick={() => { if (window.confirm('현재 탭의 데이터를 기본값으로 복원하시겠습니까?')) resetCurrentTab(); }}
              className="text-[11px] px-3 py-1 rounded border border-gray-300 text-gray-500 hover:bg-gray-50 transition-colors"
            >기본값 복원</button>
          </div>
          <button
            onClick={onClose}
            className="text-[11px] px-4 py-1.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors"
          >
            닫기
          </button>
        </div>
      </div>
    </div>
  );
};

export default MappingTableViewer;
